import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, TextField, Box, Snackbar, Alert, useTheme, useMediaQuery, IconButton, Tooltip, ButtonGroup } from '@mui/material';
import { Add as AddIcon, HelpOutline as HelpOutlineIcon, Edit as EditIcon } from '@mui/icons-material';
import  {publicInstance} from '../axiosInstance';
import DeviceTable from './DeviceTable';
import { Device } from './types';
import { Link, useLocation, useNavigate } from 'react-router-dom';

const HomePage: React.FC = () => {
  const [devices, setDevices] = useState<Device[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const location = useLocation();
  const [role, setRole] = useState<string | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  useEffect(() => {
    const storedRole = localStorage.getItem("role");
    setRole(storedRole);

    publicInstance.get('/dacapi_sensorbox_introduction_form')
      .then(response => {
        setDevices(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching devices:', error);
        setLoading(false);
      });

    if (location.state?.successMessage) {
      setSnackbarMessage(location.state.successMessage);
      setSnackbarOpen(true);
    }
  }, [location.state]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valueWithoutSpaces = e.target.value.replace(/\s+/g, '');
    setSearch(valueWithoutSpaces);
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      navigate(`/device/${search}`);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDelete = (deviceNumber: number) => {
    setDevices((prevDevices) => prevDevices.filter((device) => device.box !== deviceNumber));
  };

  return (
    <Box sx={{ backgroundColor: 'background.default', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false}>
        <Box 
          display="flex" 
          alignItems="center" 
          mb={2} 
          gap={3}
          flexDirection={isMobile ? 'column' : 'row'}
        >
          <Typography variant="h6" sx={{ marginBottom: isMobile ? 2 : 0, minWidth: 'fit-content' }}>
            {`${devices.length} Devices`}
          </Typography>
          
          <TextField
            label="Search by Device Number"
            value={search}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            variant="outlined"
            size="small"
            sx={{ 
              width: isMobile ? '100%' : 250,
              backgroundColor: 'white',
              marginBottom: isMobile ? 2 : 0,
            }}
          />

          <ButtonGroup 
            variant="contained" 
            sx={{ 
              marginLeft: isMobile ? 0 : '24px',
              width: isMobile ? '100%' : 'auto',
              '& .MuiButton-root': {
                textTransform: 'none',
                px: 2,
                width: isMobile ? '100%' : 'auto',
                whiteSpace: 'nowrap'
              },
              boxShadow: 1,
            }}
          >
            <Button
              startIcon={<AddIcon />}
              component={Link}
              to="/add-device"
            >
              Add New Device
            </Button>
            <Button
              startIcon={<EditIcon />}
              component={Link}
              to="/bulk-edit"
            >
              Bulk Edit Devices
            </Button>
            {(role === 'admin' || role === 'serwis+') && (
              <Button
                startIcon={<AddIcon />}
                component={Link}
                to="/menu-items-manager"
              >
                Menu Items Manager
              </Button>
            )}
          </ButtonGroup>
        </Box>
        <DeviceTable devices={devices.filter(device => device.box.toString().includes(search))} loading={loading} onDelete={handleDelete} />
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
        >
          <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default HomePage;
